import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type { SystemThought } from "@/types/WorkflowInstance";
import type { RootState } from "@/app/store";
import type { WorkflowInstance } from "@/types";

export interface RequestAdditionalInformationState {
  markedAsNotRequired: boolean;
  filesMap: string[]; // File names only for SignalR sync
  replyOpen: boolean;
  formValues: string;
}

export interface AdditionalInformationState {
  byRequestId: Record<string, RequestAdditionalInformationState>;
}

export interface WorkflowState {
  currentWorkflowInstanceId: string | null;
  additionalInformation: AdditionalInformationState;
  // Store system thoughts per step instance
  systemThoughtsByStepInstance: Record<string, SystemThought[]>;
  status: "idle" | "loading" | "loaded" | "error";
  // Track if user submitted and waiting for agent response
  userSubmittedAwaitingResponse: boolean;
  // Step selection state
  selectedStepInstanceId: string | null;
}

const initialState: WorkflowState = {
  currentWorkflowInstanceId: null,
  additionalInformation: {
    byRequestId: {},
  },
  systemThoughtsByStepInstance: {},
  status: "idle",
  userSubmittedAwaitingResponse: false,
  selectedStepInstanceId: null,
};

export const workflowSlice = createSlice({
  name: "workflow",
  initialState,
  reducers: {
    updateRequestAdditionalInformation: (
      state,
      action: PayloadAction<{
        requestId: string;
        updates: Partial<RequestAdditionalInformationState>;
      }>,
    ) => {
      const { requestId, updates } = action.payload;
      if (!state.additionalInformation.byRequestId[requestId]) {
        state.additionalInformation.byRequestId[requestId] = {
          markedAsNotRequired: false,
          filesMap: [],
          replyOpen: false,
          formValues: "",
        };
      }
      Object.assign(
        state.additionalInformation.byRequestId[requestId],
        updates,
      );
    },
    updateAdditionalInformationBatch: (
      state,
      action: PayloadAction<
        Record<string, Partial<RequestAdditionalInformationState>>
      >,
    ) => {
      Object.entries(action.payload).forEach(([requestId, updates]) => {
        if (!state.additionalInformation.byRequestId[requestId]) {
          state.additionalInformation.byRequestId[requestId] = {
            markedAsNotRequired: false,
            filesMap: [],
            replyOpen: false,
            formValues: "",
          };
        }
        Object.assign(
          state.additionalInformation.byRequestId[requestId],
          updates,
        );
      });
    },
    clearRequestAdditionalInformation: (
      state,
      action: PayloadAction<string>, // requestId
    ) => {
      delete state.additionalInformation.byRequestId[action.payload];
    },
    clearAllAdditionalInformation: (state) => {
      state.additionalInformation.byRequestId = {};
    },
    setSystemThoughts: (
      state,
      action: PayloadAction<{
        workflowInstanceId: string;
        thoughtsByStepInstance: Record<string, SystemThought[]>;
      }>,
    ) => {
      state.currentWorkflowInstanceId = action.payload.workflowInstanceId;

      const uniqueThoughts: Record<string, SystemThought[]> = {};
      Object.entries(action.payload.thoughtsByStepInstance).forEach(
        ([stepInstanceId, thoughts]) => {
          uniqueThoughts[stepInstanceId] = thoughts.filter((thought, index) => {
            if (
              index > 0 &&
              thoughts[index - 1].description === thought.description
            ) {
              return false;
            }
            return true;
          });
        },
      );
      state.systemThoughtsByStepInstance = uniqueThoughts;
      state.status = "loaded";
    },
    appendSystemThought: (
      state,
      action: PayloadAction<{
        stepInstanceId: string;
        thought: SystemThought;
      }>,
    ) => {
      // Only append if it's for the current workflow instance
      if (state.currentWorkflowInstanceId) {
        const { stepInstanceId, thought } = action.payload;
        if (!state.systemThoughtsByStepInstance[stepInstanceId]) {
          state.systemThoughtsByStepInstance[stepInstanceId] = [];
        }
        // Prevent sequential duplicates: only skip if the last thought has the same description
        const thoughtsList = state.systemThoughtsByStepInstance[stepInstanceId];
        const lastThought =
          thoughtsList.length > 0
            ? thoughtsList.at(-1)!
            : null;
        if (lastThought?.description !== thought.description) {
          state.systemThoughtsByStepInstance[stepInstanceId].push(thought);
          state.userSubmittedAwaitingResponse = false;
        }
      }
    },
    resetWorkflow: (state) => {
      state.currentWorkflowInstanceId = null;
      state.systemThoughtsByStepInstance = {};
      state.additionalInformation.byRequestId = {};
      state.status = "idle";
      state.userSubmittedAwaitingResponse = false;
      state.selectedStepInstanceId = null;
    },
    setWorkflowLoading: (state) => {
      state.status = "loading";
    },
    setUserSubmittedAwaitingResponse: (
      state,
      action: PayloadAction<boolean>,
    ) => {
      state.userSubmittedAwaitingResponse = action.payload;
    },
    setSelectedStepInstanceId: (
      state,
      action: PayloadAction<string | null>,
    ) => {
      state.selectedStepInstanceId = action.payload;
    },
    clearSelectedStepInstanceId: (state) => {
      state.selectedStepInstanceId = null;
    },
  },
});

export const {
  updateRequestAdditionalInformation,
  updateAdditionalInformationBatch,
  clearRequestAdditionalInformation,
  clearAllAdditionalInformation,
  setSystemThoughts,
  appendSystemThought,
  resetWorkflow,
  setWorkflowLoading,
  setUserSubmittedAwaitingResponse,
  setSelectedStepInstanceId,
  clearSelectedStepInstanceId,
} = workflowSlice.actions;

export const workflowReducer = workflowSlice.reducer;

// Selectors
export const selectAdditionalInformationState = (state: RootState) =>
  state.workflow.additionalInformation;

export const selectRequestAdditionalInformation = (
  state: RootState,
  requestId: string,
) => state.workflow.additionalInformation.byRequestId[requestId];

export const selectAllRequestsAdditionalInformation = (state: RootState) =>
  state.workflow.additionalInformation.byRequestId;

export const selectAllSystemThoughts = (state: RootState) =>
  state.workflow.systemThoughtsByStepInstance;

export const selectCurrentWorkflowInstanceId = (state: RootState) =>
  state.workflow.currentWorkflowInstanceId;

export const selectWorkflowStatus = (state: RootState) => state.workflow.status;

export const selectSystemThoughtsByStepInstanceId = (
  state: RootState,
  stepInstanceId: string,
) => state.workflow.systemThoughtsByStepInstance[stepInstanceId] ?? [];

export const selectLatestSystemThought = (state: RootState) => {
  const allThoughts = Object.values(
    state.workflow.systemThoughtsByStepInstance,
  ).flat();
  if (allThoughts.length === 0) return null;
  return allThoughts.at(-1)!;
};

export const selectUserSubmittedAwaitingResponse = (state: RootState) =>
  state.workflow.userSubmittedAwaitingResponse;

export const selectSelectedStepInstanceId = (state: RootState) =>
  state.workflow.selectedStepInstanceId;

export const selectCurrentStepInstance = (
  workflow: WorkflowInstance | undefined,
  selectedStepInstanceId: string | null,
) => {
  if (!workflow) return undefined;

  // If we have a selected step, return it
  if (selectedStepInstanceId) {
    const selectedStep = workflow.workflowStepInstances.find(
      (step) => step.id === selectedStepInstanceId,
    );
    if (selectedStep) {
      return selectedStep;
    }
  }

  // Return last completed step
  if (workflow.status === "Completed") {
    return (
      workflow.workflowStepInstances
        .filter((step) => step.status === "Completed")
        .sort((a, b) => b.stepOrder - a.stepOrder)[0] ?? null
    );
  }

  // Wondering if we need to cover pending steps too? Will look into later.

  // Otherwise return the first InProgress or Failed step
  return workflow.workflowStepInstances.find(
      (step) => step.status === "InProgress" || step.status === "Failed",
  );
};
